import Sidebar from '../utill/Sidebar'
import Header from "../utill/Header";
import Footer from "../utill/Footer";
import {Button, Card, Form} from "react-bootstrap";
import {ChangeEvent, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Url from "../utill/Url";
import {useSelector} from "react-redux";
import * as React from 'react';
import {ResponsiveChartContainer} from '@mui/x-charts/ResponsiveChartContainer';
import {BarPlot} from '@mui/x-charts/BarChart';
import {LinePlot, MarkPlot} from '@mui/x-charts/LineChart';
import {ChartsXAxis} from '@mui/x-charts/ChartsXAxis';
import {ChartsYAxis} from '@mui/x-charts/ChartsYAxis';
import {ChartsLegend} from '@mui/x-charts/ChartsLegend';
import {ChartsTooltip} from '@mui/x-charts/ChartsTooltip';
import {ChartsAxisHighlight} from '@mui/x-charts/ChartsAxisHighlight';
import type {AxisConfig} from '@mui/x-charts/models';
import {reissue} from "../utill/UtilComponent";
import axios from "axios";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DemoContainer} from "@mui/x-date-pickers/internals/demo";
import dayjs, {Dayjs} from "dayjs";
import {Paper, Table, TableBody, TableContainer, TableHead, TableRow, useMediaQuery} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import Loading from "../utill/Loading";
import {RootState} from "../../redux/store";
import StyledTableCell from "../utill/StyledTableCell"
import '../../css/RubieHudAdChart.css';

// 루비 허드 광고 통계(STAT0400): 일별 노출(tb_mcon_ad_impression_daily)·클릭(tb_mcon_ad_click_log)

interface AdItem {
    adId: number;
    adName: string;
    adType: string;
    startDate: string;  // yyyyMMddHHmmss (KST)
    endDate: string;
    active: boolean;
}

interface CountItem {
    impressions: number;
    clicks: number;
    clickUsers: number;
}

interface DailyItem extends CountItem {
    date: string;       // yyyy-MM-dd (KST)
}

interface BreakdownItem extends CountItem {
    adId: number;
    adName: string;
    modelId: string;
    modelName: string;
    osType: string;
}

const IMPRESSION_COLOR = '#4e73df';
const CLICK_COLOR = '#e74a3b';
const CTR_COLOR = '#1cc88a';

// 루비 허드 광고 서비스 시작일. 이전 날짜는 데이터가 없어 고르지 못하게 한다.
const SERVICE_START = dayjs('2026-11-05');
// 기본 시작일: 이번 달 1일(단, 서비스 시작일보다 앞서지 않게), 종료일은 오늘
const DEFAULT_START = dayjs().startOf('month').isBefore(SERVICE_START)
    ? SERVICE_START : dayjs().startOf('month');

function format(num: number) {
    return (num ?? 0).toLocaleString();
}

function ctr(clicks: number, impressions: number) {
    return impressions > 0 ? (clicks / impressions * 100).toFixed(2) + '%' : '-';
}

function periodText(ad: AdItem) {
    const f = (v: string) => v && v.length >= 8 ? `${v.substring(0, 4)}-${v.substring(4, 6)}-${v.substring(6, 8)}` : v;
    return `${f(ad.startDate)} ~ ${f(ad.endDate)}`;
}

function RubieHudAdChart() {
    const url = useContext(Url)
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user.value);
    const auth = useSelector((state: RootState) => state.loginAuth.value);
    const [startDate, setStartDate] = useState<Dayjs>(DEFAULT_START);
    const [endDate, setEndDate] = useState<Dayjs>(dayjs().startOf('day'));
    const [adId, setAdId] = useState('');
    const [ads, setAds] = useState<AdItem[]>([]);
    const [summary, setSummary] = useState<CountItem>({impressions: 0, clicks: 0, clickUsers: 0});
    const [daily, setDaily] = useState<DailyItem[]>([]);
    const [breakdown, setBreakdown] = useState<BreakdownItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    // 휴대폰 화면(RubieHudAdChart.css 의 기준과 같은 폭)
    const isMobile = useMediaQuery('(max-width:767.98px)');

    let userId = user.id;

    const search = () => {
        setLoading(true);
        reissue(userId, url, navigate).then(() => {
            axios.get(url + 'statistic/rubie-ad', {
                headers: {
                    'Authorization': localStorage.getItem('Authorization')
                },
                params: {
                    startDate: startDate.format('YYYYMMDD'),
                    endDate: endDate.format('YYYYMMDD'),
                    adId: adId === '' ? undefined : adId,
                }
            }).then((response) => {
                setLoading(false);
                if (response.data.code !== 200 || !response.data.result) {
                    alert(response.data.message || '조회에 실패했습니다.');
                    return;
                }
                const result = response.data.result;
                setAds(result.ads);
                setSummary(result.summary);
                setDaily(result.daily);
                setBreakdown(result.breakdown);
            }).catch(() => {
                setLoading(false);
                alert('조회에 실패했습니다.');
            })
        })
    }

    useEffect(() => {
        document.title = 'Mcon 모니터링 시스템-루비 허드 광고 통계'
        if (auth.MENUAUTH?.STAT0400?.readAuthorityYn !== "1") {
            alert('페이지를 볼 수 있는 권한이 없습니다.');
            navigate(auth.firstUrl);
            return;
        }
        search();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStartDateChange = (newValue: Dayjs | null) => {
        if (!newValue) return;
        setStartDate(newValue);
    };

    const handleEndDateChange = (newValue: Dayjs | null) => {
        if (!newValue) return;
        if (newValue.isBefore(startDate)) {
            setStartDate(newValue);
        }
        setEndDate(newValue);
    };

    const handleAdChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setAdId(e.target.value);
    };

    const summaryItems = [
        {title: '노출 수', value: format(summary.impressions), color: IMPRESSION_COLOR},
        {title: '클릭 수', value: format(summary.clicks), color: CLICK_COLOR},
        {title: '클릭률', value: ctr(summary.clicks, summary.impressions), color: '#1cc88a'},
        {title: '클릭 사용자 수', value: format(summary.clickUsers), color: '#36b9cc'},
    ];

    return (
        <div id="wrapper" className={'rubie-ad-page' + (menuOpen ? ' menu-open' : '')}>
            <Sidebar menuCode={"STAT0400"}/>
            <div className="rubie-ad-backdrop" onClick={() => setMenuOpen(false)}/>
            <div id="content-wrapper" className="d-flex flex-column">
                <div id="content">
                    <div className="container-fluid">
                        <button type="button" className="rubie-ad-menu-btn" onClick={() => setMenuOpen(true)}>☰ 메뉴</button>
                        <Header pageName='루비 허드 광고 통계'/>
                        <Card className="card shadow mb-4">
                            <Card.Header className="py-3">
                                <h6 className="m-0 font-weight-bold text-primary">루비 허드 광고 통계 검색</h6>
                                <p className="mb-2 mt-2 basic_font">일자(한국 시간) 기준으로 광고 노출 수와 클릭 수를 조회 합니다.</p>
                                {loading ? <Loading/> : null}
                                <Form id="searchForm">
                                    <div className='form-group row'>
                                        <div className='search_area'>
                                            <Form.Select className='select_box' style={{marginTop: '8px', width: isMobile ? '100%' : 'auto'}}
                                                         value={adId} onChange={handleAdChange}>
                                                <option value="">전체 광고</option>
                                                {ads.map(ad => (
                                                    <option key={ad.adId} value={String(ad.adId)}>
                                                        {`#${ad.adId} ${ad.adName}${ad.active ? '' : ' (중지)'}`}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko-KR">
                                                <DemoContainer components={['DatePicker', 'DatePicker']} sx={{marginLeft: isMobile ? 0 : '1rem'}}>
                                                    <DatePicker
                                                        label="시작일자"
                                                        format='YYYY-MM-DD'
                                                        slotProps={{textField: {size: 'small'}}}
                                                        value={startDate}
                                                        onChange={handleStartDateChange}
                                                        minDate={SERVICE_START}
                                                        shouldDisableDate={(day) => day > endDate}
                                                    />
                                                    <DatePicker
                                                        label="종료일자"
                                                        format='YYYY-MM-DD'
                                                        slotProps={{textField: {size: 'small'}}}
                                                        value={endDate}
                                                        onChange={handleEndDateChange}
                                                        minDate={SERVICE_START}
                                                        disableFuture
                                                    />
                                                </DemoContainer>
                                            </LocalizationProvider>
                                        </div>
                                        <div className='search_button_area' style={{paddingTop: '8px'}}>
                                            <Button variant="primary" className='search_button'
                                                    onClick={search}>검색</Button>
                                        </div>
                                    </div>
                                </Form>
                            </Card.Header>
                            <Card.Body>
                                <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
                                    {summaryItems.map(item => (
                                        <Card key={item.title} className="shadow-sm"
                                              style={{flex: '1 1 140px', borderLeft: `0.25rem solid ${item.color}`}}>
                                            <Card.Body className="py-3">
                                                <div style={{fontSize: '0.8rem', fontWeight: 700, color: item.color}}>{item.title}</div>
                                                <div style={{fontSize: '1.4rem', fontWeight: 700, color: '#5a5c69'}}>{item.value}</div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            </Card.Body>
                            <Card.Body>
                                <h6 className="font-weight-bold text-primary mb-3">일자별 노출 · 클릭 · 클릭률</h6>
                                {/* x-charts 6.19 막대 차트는 데이터가 빈 배열이면 축 범위 계산에서 오류가 나 화면 전체가 멈춘다.
                                    조회 결과가 온 뒤(하루 이상)에만 그린다. */}
                                {daily.length > 0 && (
                                <>
                                        <div style={{width: 'auto', height: isMobile ? '18rem' : '24rem'}}>
                                            <ResponsiveChartContainer
                                                margin={isMobile ? {top: 50, right: 40, bottom: 30, left: 45} : {top: 50, right: 70, bottom: 30, left: 70}}
                                                xAxis={[{
                                                    id: 'date',
                                                    scaleType: 'band',
                                                    data: daily.map(d => d.date.substring(5)),
                                                    // 휴대폰에서는 날짜 글자가 겹치지 않게 5개 안팎만 표시
                                                    tickLabelInterval: isMobile
                                                        ? (_: any, i: number) => i % Math.ceil(daily.length / 5) === 0
                                                        : 'auto',
                                                    // 날짜가 적으면 막대가 너무 넓어지지 않게 빈칸 비율을 키운다.
                                                    categoryGapRatio: daily.length <= 1 ? 0.8 : daily.length <= 3 ? 0.6 : 0.4,
                                                    barGapRatio: 0.1,
                                                } as AxisConfig<'band'>]}
                                                yAxis={[
                                                    {id: 'impression', min: 0, valueFormatter: (v: number) => v.toLocaleString()},
                                                    {id: 'click', min: 0, valueFormatter: (v: number) => v.toLocaleString()},
                                                    {id: 'ctr', min: 0},
                                                ]}
                                                series={[
                                                    {
                                                        type: 'bar',
                                                        id: 'impression',
                                                        label: '노출',
                                                        yAxisKey: 'impression',
                                                        color: IMPRESSION_COLOR,
                                                        data: daily.map(d => d.impressions),
                                                        valueFormatter: (v: number | null) => format(v ?? 0),
                                                    },
                                                    {
                                                        type: 'bar',
                                                        id: 'click',
                                                        label: '클릭',
                                                        yAxisKey: 'click',
                                                        color: CLICK_COLOR,
                                                        data: daily.map(d => d.clicks),
                                                        valueFormatter: (v: number | null) => format(v ?? 0),
                                                    },
                                                    {
                                                        type: 'line',
                                                        id: 'ctr',
                                                        label: '클릭률',
                                                        yAxisKey: 'ctr',
                                                        color: CTR_COLOR,
                                                        curve: 'linear',
                                                        data: daily.map(d => d.impressions > 0 ? Number((d.clicks / d.impressions * 100).toFixed(2)) : null),
                                                        valueFormatter: (v: number | null) => v == null ? '-' : `${v.toFixed(2)}%`,
                                                    },
                                                ]}
                                            >
                                                <BarPlot/>
                                                <LinePlot/>
                                                <MarkPlot/>
                                                <ChartsXAxis axisId="date" position="bottom"/>
                                                <ChartsYAxis axisId="impression" position="left"
                                                             tickLabelStyle={{fill: IMPRESSION_COLOR}}/>
                                                <ChartsYAxis axisId="click" position="right"
                                                             tickLabelStyle={{fill: CLICK_COLOR}}/>
                                                <ChartsLegend/>
                                                <ChartsAxisHighlight x="band"/>
                                                <ChartsTooltip trigger="axis"/>
                                            </ResponsiveChartContainer>
                                        </div>
                                        <p className="basic_font" style={{textAlign: 'center', color: '#858796', marginBottom: 0}}>
                                            왼쪽 축(파랑) 노출 수 · 오른쪽 축(빨강) 클릭 수 · 초록 선 클릭률(값은 그래프에 마우스를 올리면 표시)
                                        </p>
                                </>
                                )}
                            </Card.Body>
                            <Card.Body>
                                <h6 className="font-weight-bold text-primary mb-3">일자별</h6>
                                <TableContainer component={Paper}>
                                    <Table style={{marginBottom: 0}} sx={{minWidth: 460}} size="small" aria-label="daily table">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>일자</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>노출 수</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>클릭 수</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>클릭률</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>클릭 사용자 수</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[...daily].reverse().map(row => (
                                                <TableRow key={row.date}>
                                                    <StyledTableCell align="center">{row.date}</StyledTableCell>
                                                    <StyledTableCell align="center">{format(row.impressions)}</StyledTableCell>
                                                    <StyledTableCell align="center">{format(row.clicks)}</StyledTableCell>
                                                    <StyledTableCell align="center">{ctr(row.clicks, row.impressions)}</StyledTableCell>
                                                    <StyledTableCell align="center">{format(row.clickUsers)}</StyledTableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card.Body>
                            <Card.Body>
                                <h6 className="font-weight-bold text-primary mb-3">광고 · HUD 모델 · OS 별 합계</h6>
                                <TableContainer component={Paper}>
                                    <Table style={{marginBottom: 0}} sx={{minWidth: 760}} size="small" aria-label="breakdown table">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>광고</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>HUD 모델</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>OS</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>노출 수</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>클릭 수</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>클릭률</StyledTableCell>
                                                <StyledTableCell align="center" sx={{whiteSpace: 'nowrap'}}>클릭 사용자 수</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {breakdown.length === 0 ? (
                                                <TableRow>
                                                    <StyledTableCell align="center" colSpan={7}>조회 기간에 데이터가 없습니다.</StyledTableCell>
                                                </TableRow>
                                            ) : breakdown.map(row => {
                                                const ad = ads.find(a => a.adId === row.adId);
                                                return (
                                                    <TableRow key={`${row.adId}|${row.modelId}|${row.osType}`}>
                                                        <StyledTableCell align="center">
                                                            {`#${row.adId} ${row.adName}`}
                                                            {ad ? <div style={{fontSize: '0.75rem', color: '#858796'}}>{periodText(ad)}</div> : null}
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center">{`${row.modelId} · ${row.modelName}`}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.osType === 'IOS' ? 'iOS' : row.osType === 'ANDROID' ? 'Android' : row.osType}</StyledTableCell>
                                                        <StyledTableCell align="center">{format(row.impressions)}</StyledTableCell>
                                                        <StyledTableCell align="center">{format(row.clicks)}</StyledTableCell>
                                                        <StyledTableCell align="center">{ctr(row.clicks, row.impressions)}</StyledTableCell>
                                                        <StyledTableCell align="center">{format(row.clickUsers)}</StyledTableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
                <Footer/>
            </div>
        </div>
    )
}

export default RubieHudAdChart;
